/**
 * `/settings/workspace` — general workspace settings.
 *
 * Clerk owns the org name; Supabase mirrors slug + plan. The
 * workspace surface the user edits here is Clerk's org (name, slug)
 * — the management endpoint `PATCH /v1/management/orgs/{uuid}`
 * mirrors slug into our DB so external-facing URLs stay in sync.
 *
 * Timezone + default region are stored on organisation_settings but
 * the dashboard hook for those doesn't exist yet (Gap M9). We
 * render them as disabled inputs with tooltip copy so the UX
 * footprint is stable for when the backend ships.
 */
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toastSuccess } from '@/lib/errors';
import { mapClerkError } from '@/lib/mapClerkError';
import { useWorkspace } from '@/lib/queries';
import { useOrganization } from '@clerk/react';
import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/settings/workspace')({
  component: WorkspaceSettings,
});

function WorkspaceSettings(): ReactElement {
  const { organization, isLoaded } = useOrganization();
  const workspace = useWorkspace();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!organization) return;
    setName(organization.name ?? '');
    setSlug(organization.slug ?? '');
  }, [organization]);

  if (!isLoaded || !organization) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full max-w-xl" />
        <Skeleton className="h-32 w-full max-w-xl" />
      </div>
    );
  }

  const activeOrg = workspace.data?.organisations.find(
    (o) => o.auth_provider_id === organization.id,
  );
  const isAdmin = activeOrg?.role === 'admin';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organization) return;
    setFieldErrors({});
    setFormError(undefined);
    setSubmitting(true);
    try {
      await organization.update({
        name: name.trim(),
        slug: slug.trim(),
      });
      toastSuccess('Workspace updated');
    } catch (err) {
      const mapped = mapClerkError(err);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-8">
      <section>
        <header className="mb-4">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">General</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Your workspace's public-facing details. {isAdmin ? '' : 'Only admins can edit.'}
          </p>
        </header>
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <Field>
            <FieldLabel htmlFor="org-name">Workspace name</FieldLabel>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
              disabled={!isAdmin}
              required
            />
            {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="org-slug">URL slug</FieldLabel>
            <Input
              id="org-slug"
              value={slug}
              onChange={(e) => setSlug((e.target as HTMLInputElement).value)}
              disabled={!isAdmin}
              aria-describedby="org-slug-hint"
            />
            <p id="org-slug-hint" className="text-xs text-[var(--color-mute)]">
              Changing this updates team-scoped URLs — existing links keep working via redirects for
              30 days.
            </p>
            {fieldErrors.slug && <FieldError>{fieldErrors.slug}</FieldError>}
          </Field>
          {formError && (
            <div
              className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
              role="alert"
            >
              {formError}
            </div>
          )}
          {isAdmin && (
            <Button type="submit" loading={submitting}>
              Save changes
            </Button>
          )}
        </form>
      </section>

      <div className="h-px w-full bg-[var(--color-line)]" />

      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Defaults</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Timezone and default monitor region. We surface these to keep the settings slot real;
            editing goes live once the backend endpoint ships.
          </p>
        </header>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="tz">Timezone</FieldLabel>
            <Select value="Europe/London" disabled>
              <SelectTrigger id="tz">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Europe/London">Europe/London</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="region">Default region</FieldLabel>
            <Select value="europe-west2" disabled>
              <SelectTrigger id="region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="europe-west2">europe-west2</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>
    </div>
  );
}
